import React, { FC, useCallback, useState } from 'react';
import { UserApp } from '../../../models/User';
import { useDispatch } from '../../../redux/store';
import { invokeFastApi } from '../../../scripts/apis/fastapi';
import { useFlag, useUserSafe } from '../../../scripts/hooks';
import {
  ManualFetchDataType,
  useManualDataFetch,
} from '../../../scripts/hooks/manual-trigger-hooks';
import { useToaster } from '../../../scripts/hooks/toast';
import { logError } from '../../../scripts/utils';
import { Modal } from '../../controls/ui/Modal/Modal';
import { UIButton } from '../../controls/ui/UIButton/UIButton';
import { UIIcon } from '../../controls/ui/UIIcon/UIIcon';
import { UITextbox } from '../../controls/ui/UITextbox/UITextbox';
import { Switch } from '../../shadcn/lib/components/switch';

interface AppsSettingModalProps {
  app: UserApp;
  closeModal: () => void;
}

export const AppsSettingModal: FC<AppsSettingModalProps> = ({
  app,
  closeModal,
}) => {
  const [checked, setChecked] = useState(!!app.botOrgWideAccess);
  const [connectionName, setConnectionName] = useState(app.connectionName);
  const [connectionInstalledError, setConnectionInstalledError] =
    useState(false);

  const [loading, setLoading] = useState(false);
  const toaster = useToaster();
  const isAdmin = useUserSafe((user) => user.admin);
  const botsAppConnectionsEnabled = useFlag('botsAppConnections');
  const dispatch = useDispatch();

  const refetchApps = useManualDataFetch(dispatch, ManualFetchDataType.APPS);

  const handleEnabledBotChange = useCallback(() => {
    setChecked((prev) => !prev);
  }, [setChecked]);

  const handleSave = useCallback(async () => {
    if (!connectionName) {
      setConnectionInstalledError(true);
      return;
    }

    setLoading(true);
    try {
      await invokeFastApi({
        method: 'PATCH',
        path: `/app/${app.id}`,
        body: {
          connection_name: connectionName,
          enable_bots_org_wide: checked,
        },
      });

      await refetchApps();

      toaster.success('App settings updated successfully');
      closeModal();
    } catch (error) {
      logError(error);
      toaster.failure('Failed to update app settings');
    }

    setLoading(false);
  }, [app, checked, closeModal, connectionName, refetchApps, toaster]);

  return (
    <Modal modalClasses="max-w-xl" onClose={closeModal}>
      <div className="flex flex-col gap-6">
        <div className="flex gap-1 items-center">
          <UIIcon name="cog" size={16} />
          <h2 className="text-lg m-0 font-semibold">Settings</h2>
        </div>
        <div>
          <div className="text-sm font-medium">Connection Name</div>
          <UITextbox
            error={connectionInstalledError ? 'This field is required' : ''}
            onChange={setConnectionName}
            value={connectionName}
          />
        </div>
        {botsAppConnectionsEnabled && isAdmin && (
          <div className="flex gap-2">
            <div>
              <div className="text-sm font-medium">
                Enable Org-Wide Bot Access
              </div>
              <div className="text-gray-50 text-sm whitespace-normal">
                Allow anyone in the organization to create bots using this app
                connection. We recommend picking an identifiable connection name
                for this.
              </div>
            </div>
            <Switch
              checked={checked}
              onCheckedChange={handleEnabledBotChange}
            />
          </div>
        )}
      </div>
      <footer className="flex justify-between mt-6">
        <UIButton onClick={closeModal} type="secondary">
          Cancel
        </UIButton>
        <UIButton onClick={handleSave} processing={loading}>
          Save
        </UIButton>
      </footer>
    </Modal>
  );
};
